import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useField } from "formik";
import { v4 as uuid } from "uuid";
import styles from "../RuleForm.module.scss";
import { PlayIcon, PlusIcon } from "../../../../../icons";
import {
    TransactionRuleActionFormSchema,
    TransactionRuleFormSchema,
} from "../constants";
import { Button } from "../../../../general/Button/Button";
import { useWorkspaceRuleGroups } from "../../../../../api/rules.api";
import { RuleActionForm } from "./RuleActionForm";

const ACCOUNTING_RULE_ACTIONS_COUNT = 1;
const BUSINESS_RULE_ACTIONS_COUNT = 2;

export const RuleActionsForm = () => {
    const ruleGroups = useWorkspaceRuleGroups();
    const [{ value: actions }, , { setValue: setActions }] =
        useField<TransactionRuleActionFormSchema[]>("actions");
    const [{ value: groupId }] =
        useField<TransactionRuleFormSchema["groupId"]>("groupId");

    const addAction = useCallback(() => {
        setActions([...actions, { id: uuid() }]);
    }, [actions, setActions]);

    const removeAction = useCallback(
        (index: number) => {
            setActions(actions.filter((_, i) => i !== index));
        },
        [actions, setActions],
    );

    const group = useMemo(
        () => ruleGroups.data?.ruleGroups?.find((g) => g.id === groupId),
        [ruleGroups.data?.ruleGroups, groupId],
    );

    // At the moment we support 2 different actions, and you can not add duplicates.
    const canAddAction = useMemo(() => {
        if (!group) {
            return false;
        }

        if (group.isAccounting) {
            return actions.length < ACCOUNTING_RULE_ACTIONS_COUNT;
        }

        return actions.length < BUSINESS_RULE_ACTIONS_COUNT;
    }, [actions.length, group]);

    return (
        <section className={styles.actions}>
            <header className={styles.header}>
                <aside
                    className={classNames(
                        styles.sectionIcon,
                        styles.sectionIconActions,
                    )}
                >
                    <PlayIcon className="icon-color-blue-600" />
                </aside>
                <h5>Action</h5>
            </header>

            <main className={styles.content}>
                <div className={styles.sectionRows}>
                    {actions.map((action, index) => (
                        <div className={styles.sectionRow} key={action.id}>
                            <RuleActionForm
                                index={index}
                                id={action.id}
                                onRemove={removeAction}
                            />
                        </div>
                    ))}
                    <div className={styles.sectionRow}>
                        <Button
                            variant="secondary"
                            onClick={addAction}
                            type="button"
                            disabled={!canAddAction}
                            tooltip={
                                canAddAction
                                    ? undefined
                                    : "You can add only one action per type"
                            }
                        >
                            <PlusIcon />
                            Add
                        </Button>
                    </div>
                </div>
            </main>
        </section>
    );
};
